import { Spinner } from '@hbs-us/ui';
import { type ReactNode, useEffect, useState } from 'react';

import { apiFetchJson } from '../../lib/apiFetch.js';
import { readStoredLocale } from '../../lib/locale.js';

/**
 * Boot-time session-handoff intercepts (Plan 22-29). These run BEFORE the AuthGuard tree — exactly
 * like the /join intercept — because they must SET the qelm_session cookie (via the api host, whose
 * Set-Cookie is scoped to the shared app-wildcard domain) before the /api/v1/me probe can succeed.
 *
 *   • 'impersonation' → POST /api/v1/auth/impersonation { token } (the admin handoff redemption).
 *   • 'demo'          → POST /api/v1/auth/demo (the self-serve demo Viewer session).
 *
 * On success we replaceState to '/' and mount the normal app tree (children), whose /me probe now
 * authenticates via the freshly-set cookie (and the impersonation banner renders for the admin
 * handoff). On failure we show a minimal notice with a link to the tenant login.
 *
 * Deliberately dependency-light: no i18n namespaces (this boots before the app), just the stored
 * locale + a tiny inline EN/ES string table. Token-only styling.
 */

export type HandoffMode = 'impersonation' | 'demo';

type HandoffState = 'loading' | 'success' | 'error';

interface HandoffCopy {
  loading: string;
  errorHeading: string;
  errorBody: string;
  loginCta: string;
}

const COPY: Record<'en' | 'es', Record<HandoffMode, HandoffCopy>> = {
  en: {
    impersonation: {
      loading: 'Starting session…',
      errorHeading: "We couldn't start the session",
      errorBody: 'This support session link is invalid or has expired. Please start it again.',
      loginCta: 'Go to sign in',
    },
    demo: {
      loading: 'Loading the demo…',
      errorHeading: 'Demo unavailable',
      errorBody: 'The demo workspace is not available right now. Please try again later.',
      loginCta: 'Go to sign in',
    },
  },
  es: {
    impersonation: {
      loading: 'Iniciando sesión…',
      errorHeading: 'No pudimos iniciar la sesión',
      errorBody: 'Este enlace de sesión de soporte no es válido o ha caducado. Vuelve a iniciarlo.',
      loginCta: 'Ir a iniciar sesión',
    },
    demo: {
      loading: 'Cargando la demostración…',
      errorHeading: 'Demostración no disponible',
      errorBody: 'El espacio de demostración no está disponible ahora. Inténtalo más tarde.',
      loginCta: 'Ir a iniciar sesión',
    },
  },
};

function redeem(mode: HandoffMode): Promise<{ ok: true }> {
  if (mode === 'impersonation') {
    const token =
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('token') ?? '')
        : '';
    if (!token) return Promise.reject(new Error('missing_token'));
    return apiFetchJson<{ ok: true }>('/api/v1/auth/impersonation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  }
  return apiFetchJson<{ ok: true }>('/api/v1/auth/demo', { method: 'POST' });
}

export function SessionHandoffRoute({
  mode,
  children,
}: {
  mode: HandoffMode;
  children: ReactNode;
}) {
  const [state, setState] = useState<HandoffState>('loading');
  const copy = COPY[readStoredLocale()][mode];

  useEffect(() => {
    let cancelled = false;
    void redeem(mode)
      .then(() => {
        if (cancelled) return;
        // Drop the handoff path + token from the URL before mounting the app.
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', '/');
        }
        setState('success');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  if (state === 'success') {
    return <>{children}</>;
  }

  if (state === 'error') {
    return (
      <main className="flex h-full items-center justify-center bg-background p-6">
        <section
          role="alert"
          aria-live="assertive"
          className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-lg"
        >
          <h1 className="font-semibold text-foreground text-xl">{copy.errorHeading}</h1>
          <p className="mt-2 text-muted-foreground text-sm">{copy.errorBody}</p>
          <a
            href="/login"
            className="mt-6 inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {copy.loginCta}
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="flex h-full items-center justify-center bg-background p-6">
      <Spinner label={copy.loading} />
    </main>
  );
}
